import {
  AudioSettings,
  createUserSettingsStore,
  DEFAULT_AUDIO_SETTINGS,
  sanitizeVolume,
} from '@hotpot-arcade/fanforge-core';
import { createContext, useContext, useEffect, useRef } from 'react';
import { useStore } from 'zustand';

const USER_SETTINGS_STORAGE_KEY = 'fanforge-user-settings';
const LEGACY_STORAGE_KEYS = ['meow-meow-user-settings'];

type UserSettingsProviderProps = {
  children: React.ReactNode;
};

export const UserSettingsContext = createContext<ReturnType<typeof createUserSettingsStore> | null>(null);

export const UserSettingsProvider = ({ children }: UserSettingsProviderProps) => {
  const storeRef = useRef<ReturnType<typeof createUserSettingsStore>>(null);

  if (!storeRef.current) {
    const audioFromStorage = typeof window !== 'undefined' ? readAudioFromStorage() : undefined;

    storeRef.current = createUserSettingsStore({
      audio: audioFromStorage,
    });
  }

  useEffect(() => {
    const store = storeRef.current;
    if (!store || typeof window === 'undefined') return;

    return store.subscribe((state) => state.audio);
  }, []);

  useEffect(() => {
    return () => {
      const store = storeRef.current;
      if (!store || typeof window === 'undefined') return;

      persistAudio(store.getState().audio);
    };
  });

  return <UserSettingsContext.Provider value={storeRef.current}>{children}</UserSettingsContext.Provider>;
};

export type UserSettingsViewState = {
  audio: AudioSettings;
  setMusicVolume: (volume: number) => void;
  setSoundVolume: (volume: number) => void;
};

export function useUserSettings(): UserSettingsViewState {
  const store = useContext(UserSettingsContext);
  if (!store) {
    throw new Error('useUserSettings must be used within UserSettingsProvider.');
  }

  return useStore(store);
}

function readAudioFromStorage(): Partial<AudioSettings> | undefined {
  const keysToCheck = [USER_SETTINGS_STORAGE_KEY, ...LEGACY_STORAGE_KEYS];

  for (const key of keysToCheck) {
    const storedValue = window.localStorage.getItem(key);
    if (!storedValue) continue;

    const parsed = parseAudioSettings(storedValue);
    if (!parsed) continue;

    if (key !== USER_SETTINGS_STORAGE_KEY) {
      window.localStorage.removeItem(key);
      window.localStorage.setItem(USER_SETTINGS_STORAGE_KEY, JSON.stringify(parsed));
    }

    return parsed;
  }

  return undefined;
}

function parseAudioSettings(value: string): AudioSettings | undefined {
  try {
    const parsed = JSON.parse(value);
    const musicVolume =
      typeof parsed.musicVolume === 'number' ? sanitizeVolume(parsed.musicVolume) : DEFAULT_AUDIO_SETTINGS.musicVolume;
    const soundVolume =
      typeof parsed.soundVolume === 'number' ? sanitizeVolume(parsed.soundVolume) : DEFAULT_AUDIO_SETTINGS.soundVolume;

    return { musicVolume, soundVolume };
  } catch {
    return undefined;
  }
}

function persistAudio(audio: AudioSettings) {
  try {
    window.localStorage.setItem(USER_SETTINGS_STORAGE_KEY, JSON.stringify(audio));
  } catch (error) {
    console.error('Failed to persist user audio settings.', error);
  }
}
